import { FieldLabel, Skeleton } from '@/components/ui';
import { useAppData } from '@/hooks/useAppData';
import { useCountdown } from '@/hooks/useCountdown';
import { cn } from '@/lib/cn';
import { formatGameweek, formatRank } from '@/lib/format';
import type { ContestTeam } from '@/types';

const PLACEHOLDER = '—';

/** Deadline turns urgent inside the last six hours. */
const URGENT_THRESHOLD_MS = 6 * 60 * 60 * 1000;

/** Only the deadline cell is accented; the rest inherit body colour. */
type Tone = 'deadline' | 'urgent' | 'locked' | undefined;

interface Cell {
  label: string;
  value: string;
  tone?: Tone;
  /**
   * True while the figure behind this cell is in flight. A request that failed
   * or came back empty is no longer loading, so the cell falls back to "—".
   */
  isLoading?: boolean;
}

export interface GameweekBannerProps {
  /**
   * The team this screen is about, when it is not the manager's default —
   * My Team and Transfers for a chosen contest. Its lock window drives the
   * status cell.
   */
  team?: ContestTeam | null;
}

/**
 * The gameweek status strip that sits under the page title.
 *
 * The status cell follows the manager's team when there is one: contests lock
 * fixture by fixture, so a squad can still be open after the gameweek's own
 * deadline. "Squad locked" means that team's window has closed, nothing less.
 * With no team it describes the gameweek instead.
 */
export function GameweekBanner({ team: teamOverride }: GameweekBannerProps = {}) {
  const {
    gameweek,
    summary,
    primaryTeam,
    isGameweekLoading,
    isSummaryLoading,
    isPrimaryTeamLoading,
  } = useAppData();

  const team = teamOverride ?? primaryTeam;
  const countdown = useCountdown(team ? (team.nextLockAt ?? null) : (gameweek?.deadlineAt ?? null));

  // What the status cell says, by whose clock.
  const status: { label: string; value: string; tone: Tone } | null = team
    ? !team.canEdit
      ? { label: 'Status', value: 'Squad locked', tone: 'locked' }
      : team.transfersBlocked
        ? { label: 'Status', value: 'Match under way', tone: 'locked' }
        : team.nextLockAt && !countdown.hasPassed
          ? {
              label: 'Next lock',
              value: countdown.label,
              tone: countdown.remainingMs < URGENT_THRESHOLD_MS ? 'urgent' : 'deadline',
            }
          : { label: 'Status', value: 'Open for changes', tone: 'deadline' }
    : gameweek
      ? gameweek.status === 'settled'
        ? { label: 'Status', value: 'Finished', tone: 'locked' }
        : gameweek.status === 'open' && !countdown.hasPassed
          ? {
              label: 'Deadline',
              value: countdown.label,
              tone: countdown.remainingMs < URGENT_THRESHOLD_MS ? 'urgent' : 'deadline',
            }
          : { label: 'Status', value: 'Gameweek under way', tone: 'locked' }
      : null;

  // Waits for the team when one may be coming, so the gameweek's wording does
  // not flash up first and then change.
  const isStatusLoading =
    (teamOverride === undefined && isPrimaryTeamLoading && !primaryTeam) ||
    (!status && isGameweekLoading);

  const cells: Cell[] = [
    {
      // No number when the API did not give a real one.
      label: gameweek && gameweek.id > 0 ? `Gameweek ${formatGameweek(gameweek.id)}` : 'Gameweek',
      value: gameweek?.competition ?? PLACEHOLDER,
      isLoading: isGameweekLoading && !gameweek,
    },
    {
      label: status?.label ?? 'Deadline',
      value: status?.value ?? PLACEHOLDER,
      // No tone on the placeholder, or a missing deadline would read as urgent.
      ...(status ? { tone: status.tone } : {}),
      isLoading: isStatusLoading,
    },
    {
      label: 'Current points',
      value: summary ? String(summary.gameweekPoints) : PLACEHOLDER,
      isLoading: isSummaryLoading && !summary,
    },
    {
      label: 'Overall rank',
      value: summary ? formatRank(summary.overallRank) : PLACEHOLDER,
      isLoading: isSummaryLoading && !summary,
    },
  ];

  return (
    <section className="mb-6 grid grid-cols-2 gap-px overflow-hidden rounded-panel border border-border bg-border sm:grid-cols-4">
      {cells.map((cell) => (
        <div key={cell.label} className="bg-canvas px-4 py-3.5">
          <FieldLabel>{cell.label}</FieldLabel>
          {cell.isLoading ? (
            // Sized to the line it replaces, so the strip does not resize.
            <Skeleton className="mt-1 h-[19px] w-24" />
          ) : (
            <strong
              className={cn(
                'mt-1 block text-[15px] font-extrabold tracking-[-0.3px]',
                cell.tone === 'deadline' && 'text-primary',
                cell.tone === 'urgent' && 'text-danger',
                cell.tone === 'locked' && 'text-warning',
              )}
            >
              {cell.value}
            </strong>
          )}
        </div>
      ))}
    </section>
  );
}
